import type { MapCanvasValue } from "../components/map/contexts/MapCanvasContext/MapCanvasContext.types";
import { pausePolling } from "./pausePolling";
export interface ComparisonCandidate {
  optionId?: string;
  provider: "openai" | "gemini" | "xai" | "claude";
  mode?: "regions" | "whole" | "fast";
  model: string;
  effort: string;
  status: string;
  map?: MapCanvasValue;
  warnings: string[];
  error?: string;
  elapsedMs: number;
  compileMs: number;
  areas: {
    id: string;
    label: string;
    bounds?: { x: number; y: number; width: number; height: number };
    status: string;
    error?: string;
    progress?: string;
    lastActivityAt?: number;
    elapsedMs?: number;
    usage?: {
      input_tokens?: number;
      output_tokens?: number;
      output_tokens_details?: { reasoning_tokens?: number };
    };
    /** Fast mode only. */
    kind?: "perimeter" | "walls" | "furniture" | "labels";
    tileId?: string;
    hedged?: boolean;
    cached?: boolean;
    counts?: { desks: number; rooms: number; walls: number; labels: number };
  }[];
}
export interface FloorplanComparison {
  session: string;
  status: string;
  revision: number;
  startedAt: number;
  expiresAt: number;
  preprocessMs: number;
  width: number;
  height: number;
  /** Fast mode: straight strokes found locally, [x1, y1, x2, y2] in image pixels. */
  sketch?: number[][];
  candidates: ComparisonCandidate[];
}
export const comparisonUrl = (session: string) =>
  `/api/map-import/compare?session=${encodeURIComponent(session)}`;
export async function readFloorplanComparison(
  session: string,
  signal?: AbortSignal
): Promise<FloorplanComparison> {
  const response = await fetch(comparisonUrl(session), {
    signal,
    cache: "no-store"
  });
  const data = await response.json();
  if (!response.ok) throw Error(data.error || "Unable to read comparison");
  return data;
}
/** Status reads are free, so transient failures are retried; they never start new paid work. */
async function pollFloorplanComparison(
  session: string,
  signal: AbortSignal
): Promise<FloorplanComparison> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await readFloorplanComparison(session, signal);
    } catch (error) {
      if (signal.aborted || attempt >= 2) throw error;
      await pausePolling(signal);
    }
  }
}
/**
 * Read a streamed import: one JSON snapshot per line, the last one final.
 * Closing the connection is what cancels it on the server.
 */
async function readSnapshotStream(
  response: Response,
  signal: AbortSignal,
  onUpdate: (s: FloorplanComparison) => void
): Promise<FloorplanComparison> {
  if (!response.body) throw Error("The import returned no progress stream");
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "",
    latest: FloorplanComparison | undefined;
  const readLines = (text: string) => {
    for (const line of text.split("\n")) {
      if (!line.trim()) continue;
      latest = JSON.parse(line) as FloorplanComparison;
      onUpdate(latest);
    }
  };
  try {
    while (true) {
      signal.throwIfAborted();
      const { done, value } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });
      const end = buffer.lastIndexOf("\n");
      if (end >= 0) {
        readLines(buffer.slice(0, end));
        buffer = buffer.slice(end + 1);
      }
      if (done) break;
    }
    readLines(buffer);
  } finally {
    reader.releaseLock();
  }
  if (!latest || latest.status === "in_progress")
    throw Error(
      "The connection closed before the import finished. Please try again."
    );
  return latest;
}
export async function requestFloorplanComparison(
  png: Blob,
  signal: AbortSignal,
  onUpdate: (s: FloorplanComparison) => void,
  providers: Array<
    | ComparisonCandidate["provider"]
    | "gemini-whole"
    | "openai-low"
    | "openai-sol"
    | "openai-sol-whole"
    | "openai-sol-fast"
    | "claude-whole"
    | "claude-regions"
  >,
  parallelAreas = false,
  scope: "all" | "walls" = "all",
  reasoning: "medium" | "high" = "medium"
) {
  if (!providers.length) throw Error("Select at least one provider");
  let session: string | undefined,
    complete = false;
  try {
    const response = await fetch("/api/map-import/compare", {
      method: "POST",
      headers: {
        "Content-Type": "image/png",
        "X-Map-Providers": providers.join(","),
        "X-Map-Mode": parallelAreas ? "regions" : "whole",
        "X-Map-Scope": scope,
        "X-Map-Reasoning": reasoning,
        // Run the import inside this request; see streamSession.ts.
        "X-Map-Stream": "1"
      },
      body: png,
      signal
    });
    // Hosting rejects large request bodies before the route runs (Vercel: 4.5 MB).
    if (response.status === 413)
      throw Error(
        "This floorplan is too large to upload once converted. Try a smaller or cropped export."
      );
    if (
      response.ok &&
      response.headers?.get("content-type")?.includes("application/x-ndjson")
    ) {
      const final = await readSnapshotStream(response, signal, onUpdate);
      complete = true;
      return final;
    }
    let data = await response.json();
    if (!response.ok) throw Error(data.error || "Unable to start comparison");
    session = data.session;
    if (!session) throw Error("No comparison session returned");
    while (true) {
      signal.throwIfAborted();
      onUpdate(data);
      if (data.status !== "in_progress") {
        complete = true;
        return data as FloorplanComparison;
      }
      await pausePolling(signal);
      data = await pollFloorplanComparison(session, signal);
    }
  } finally {
    // Any unfinished exit (cancel or a failed poll) must stop the paid jobs;
    // otherwise "Try again" would run a second comparison beside this one.
    if (session && !complete)
      await fetch(comparisonUrl(session), {
        method: "DELETE",
        keepalive: true,
        signal: AbortSignal.timeout(10000)
      }).catch(() => undefined);
  }
}
